import { FC, useCallback, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Preview } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { StyledButton } from "../../styled/button";

import { useApiCall } from "@framework/react-hooks";

export interface IReportDialogButtonProps {
  idReport: number;
  title: string;
}

export const ReportDialogButton: FC<IReportDialogButtonProps> = ({ idReport, title }) => {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const { fn: getReport, isLoading: isReportLoading } = useApiCall(
    async (api, idReport: number) => {
      return api
        .fetchBlob({
          url: `/report/${idReport}/pdf`,
          method: "GET",
        })
        .catch((error: any) => {
          console.error(error);
          enqueueSnackbar(formatMessage({ id: "pages.journals.reports.downloadError" }), { variant: "error" });
        });
    },
    { success: false, error: false },
  );

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setPdfUrl(null);

    const blob = await getReport(undefined, idReport);
    if (blob) {
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    }
  }, [idReport]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setPdfUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  return (
    <>
      <IconButton onClick={() => void handleOpen()} sx={{ color: "inherit", justifyContent: "flex-end" }}>
        <Preview />
      </IconButton>
      <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent sx={{ minHeight: 500, p: 0 }}>
          {isReportLoading && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 500 }}>
              <CircularProgress />
            </div>
          )}
          {pdfUrl && !isReportLoading && (
            <iframe src={pdfUrl} width="100%" height="500px" style={{ border: "none" }} title="PDF Preview" />
          )}
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleClose} sx={{ minWidth: 80, mb: 1, mr: 2 }}>
            <FormattedMessage id="form.buttons.ok" />
          </StyledButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
